/* viva-chat.component.ts  –  Option 1: outline first ➜ fill fades in */
import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  Input,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild,
  OnInit
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/services/events/event.service';
import { StorageKey } from 'src/app/services/storage/models/storage-key.enum';
import { SecureStorageService } from 'src/app/services/storage/secure-storage.service';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
// imports at top
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'viva-chat',
  templateUrl: './viva-chat.component.html',
  styleUrls: ['./viva-chat.component.scss'],
  standalone: false,

  animations: [
    trigger('bubbleFade', [
      transition(':enter', [   // fade in on *ngIf add
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [   // fade out on *ngIf remove
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class VivaChatComponent implements OnInit, OnDestroy {
  @ViewChild('bubbleRef') bubbleRef?: ElementRef<HTMLDivElement>;
  private static bubbleWasShown = false;
  private _messageText?: string | null = undefined;

  /* ────────── Public inputs ────────── */
  @Input() color = 'var(--ion-color-secondary)'; // stroke colour
  @Input() animationDuration = 3000;             // ms for outline
  @Input() fillColor = '#ffffff';                // mouth fill colour
  @Input() fillFadeTime = 2000;                  // ms for fade-in
  @Input() bubbleDuration = 5000;                // ms bubble visible
  @Input() interactive: boolean = true;
  @Input() showMoreInfoButton: boolean = true; // Control whether to show the More Info button

  @Output() onMoreInfoClick = new EventEmitter<void>();
  @Output() onBotClicked = new EventEmitter<void>();
  @Output() onCloseBubble = new EventEmitter<void>();

  /* ────────── Template refs ────────── */
  @ViewChildren('animatedPath', { read: ElementRef })
  private paths!: QueryList<ElementRef<SVGPathElement>>;

  public currentFace: VivaBotFaceState = VivaBotFaceState.happy;

  private faceTimer!: ReturnType<typeof setTimeout>;
  private faceTimout: any = null; // existing 15s reset timer

  /* bubble flag bound with *ngIf in HTML */
  public showBubble = false;
  public static _isAwake = false;
  public isOnline: boolean = true;
  private subscriptions: Subscription[] = [];

  private greetingHintText: string = '';
  private offlinetext: string = '';
  private onlineText: string = '';

  public bubbleHintHtml: string | null = null;

  /* ────────── Transient face lock (prevents overrides) ────────── */
  private isFacePinned = false;

  private _speechEnabled = true;  // default ON
  public moreButtonText?: string;

  constructor(
    private host: ElementRef<HTMLElement>,
    private _TranslatorService: TranslatorService,
    private router: Router,
  ) {
    this.greetingHintText = this._TranslatorService.translateText('viva-chat-component.messages.greeting-hint');
    this.offlinetext = this._TranslatorService.translateText('viva-chat-component.messages.gone-offline');
    this.onlineText = this._TranslatorService.translateText('viva-chat-component.messages.gone-online');
    this.moreButtonText = this._TranslatorService.translateText('viva-chat-component.messages.more-info');

    /* Network status changes */
    this.subscriptions.push(
      EventService.networkStatusChange.subscribe(async (online: boolean) => {
        if (this.isFacePinned) {
          // Do not override thinking/error
          return;
        }
        setTimeout(async () => { //debounce network prompts
          const status = await Network.getStatus();
          if (status && status.connected != this.isOnline) {
            this.isOnline = status.connected;
            this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);
            await this.showBubbleHint(this.isOnline ? this.onlineText : this.offlinetext);
          }
        }, 1000);
      })
    );

    /* External emotion changes */
    this.subscriptions.push(
      EventService.aiBotEmotionChange.subscribe(async (botFace: VivaBotFaceState) => {
        this.setFace(botFace);

        if (botFace === VivaBotFaceState.thinking) {
          this.bubbleHintHtml = '';
          this.showBubble = true;
        } else if (botFace === VivaBotFaceState.happy) {
          this.showBubble = false;
        }

        this.triggerResetTimer();
      })
    );

    this.subscriptions.push(this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        this.resetForRouteChange();
      }));

    this.subscriptions.push(
      EventService.aiBotAwakeState.subscribe(async (isAwake: boolean) => {
        if (isAwake != VivaChatComponent._isAwake) {
          VivaChatComponent._isAwake = isAwake;
          if(VivaChatComponent._isAwake){
            await this.wakeupBot();
          }
        }
      })
    );
  }


  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((a) => a.unsubscribe());
    }
    //TextToSpeech.stop().catch(() => { });
  }

  private triggerResetTimer() {
    this.replayBubbleAnim();

    if (this.faceTimout) {
      clearTimeout(this.faceTimout);
      this.faceTimout = null;
    }
    this.faceTimout = setTimeout(() => {
      // Don’t override if pinned in thinking/error
      if (this.isFacePinned) return;

      this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);
      this.showBubble = false;

      if (this.faceTimout) {
        clearTimeout(this.faceTimout);
        this.faceTimout = null;
      }
    }, 15000);
  }

  private isTransient(face: VivaBotFaceState) {
    return face === VivaBotFaceState.thinking || face === VivaBotFaceState.error;
  }

  private setFace(face: VivaBotFaceState) {
    this.currentFace = face;
    this.isFacePinned = this.isTransient(face);
  }

  get isAwake():boolean{
    return VivaChatComponent._isAwake;
  }

  /* ────────── Lifecycle ────────── */
  async ngOnInit(): Promise<void> {
    const status = await Network.getStatus();
    this.isOnline = status.connected;

    this._speechEnabled = true;
    const audioEnabled = await SecureStorageService.get(StorageKey.AIBotAutoEnabled);
    if (audioEnabled && audioEnabled == 'false') {
      this._speechEnabled = false;
    }

    this.initialisePaths();
    this.startDrawingLoop();
    // start your 30s “no-interaction” timer:
    this.startFaceTimer();


  }

  private async wakeupBot(){
    // after 3 seconds show the hello/offline hint (but don’t override pinned)
    if (!VivaChatComponent.bubbleWasShown) {
      this.setFace(VivaBotFaceState.thinking);

      VivaChatComponent.bubbleWasShown = true;
      setTimeout(async () => {
        if (this.isOnline) {
          this.setFace(VivaBotFaceState.happy);
          await this.showBubbleHint(this.greetingHintText);
        } else {
          await this.showBubbleHint(this.offlinetext);
          this.setFace(VivaBotFaceState.sad);
        }
      }, 3000);
    }
  }

  private async showBubbleHint(hintHtml: string): Promise<void> {
    this.bubbleHintHtml = hintHtml;
    this.showBubble = true;
    const textToRead = hintHtml.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '');

    if (this._speechEnabled) {
      await this.speakText(textToRead);
    }
    await this.triggerHaptic();

    setTimeout(() => {
      this.showBubble = false;
      VivaChatComponent.bubbleWasShown = true;
    }, this.bubbleDuration);
  }


  get speechEnabled(): boolean {
    return this._speechEnabled;
  }

  set speechEnabled(value: boolean) {
    this._speechEnabled = value;
    if (!value) {
      this.stopAudio();
    } else {
      if (this._messageText) {
        this.speakText(this._messageText);
      }
    }
    if (value) {
      SecureStorageService.set(StorageKey.AIBotAutoEnabled, 'true');
    } else {
      SecureStorageService.set(StorageKey.AIBotAutoEnabled, 'false');
    }
  }

  private async triggerHaptic() {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (err) {
        console.warn('Haptics not available', err);
      }
    }

  }


  @Input()
  set messageText(value: string | null | undefined) {
    this._messageText = value;

    if (value && value.length > 0) {
      // Bubble will show because of *ngIf binding
      this.triggerHaptic();
      this.speakText(value);
    }
  }
  get messageText(): string | null | undefined {
    return this._messageText;
  }

  /* ────────── Stroke-drawing setup ────────── */
  private initialisePaths(): void {
    const totalPaths = this.paths.length;
    const segmentTime = this.animationDuration / totalPaths;

    this.paths.forEach((el, index) => {
      const path = el.nativeElement;
      const length = path.getTotalLength();

      /* base styling */
      path.style.stroke = this.color;
      path.style.fill = this.fillColor;
      path.style.fillOpacity = '1'; // hidden at start
      path.style.strokeDasharray = `${length}`;
      path.style.strokeDashoffset = `${length}`;

      /* stroke animation */
      path.style.transition = `stroke-dashoffset ${segmentTime}ms ease-out ${segmentTime * index}ms`;
    });
  }

  @HostListener('click')
  onUserInteract() {
    // If they touch the bot, flip back to happy (unless pinned) and restart timer
    if (!this.isFacePinned) {
      this.showHappy();
    }
    this.startFaceTimer();
  }

  private startFaceTimer() {
    // clear any old timer
    clearTimeout(this.faceTimer);
    // after 30 000 ms of no taps, go to the sleep face (unless pinned)
    this.faceTimer = setTimeout(() => {
      if (this.isFacePinned) return;
      this.setFace(VivaBotFaceState.sleep);
    }, 30_000);
  }

  private showHappy() {
    // go immediately back to happy (unless pinned)
    if (this.isFacePinned) return;
    this.setFace(VivaBotFaceState.happy);
  }

  /* ────────── Animation loop ────────── */
  private startDrawingLoop(): void {
    this.playAnimationOnce();
    setInterval(
      () => this.playAnimationOnce(),
      this.animationDuration + 50 /* safety buffer */
    );
  }

  private playAnimationOnce(): void {
    if (!this.isVisible(this.host.nativeElement)) return;

    const segmentTime = this.animationDuration / this.paths.length;

    /* 1️⃣ draw the strokes */
    requestAnimationFrame(() => {
      this.paths.forEach(el => (el.nativeElement.style.strokeDashoffset = '0'));
    });

    /* 2️⃣ after outline is complete, fade in the fill */
    setTimeout(() => {
      if (!this.isVisible(this.host.nativeElement)) return;

      this.paths.forEach(el => {
        const path = el.nativeElement;
        path.style.transition += `, fill-opacity ${this.fillFadeTime}ms ease-in`;
        path.style.fillOpacity = '1';
      });
    }, this.animationDuration);

    /* 3️⃣ reset for next loop */
    setTimeout(() => {
      if (!this.isVisible(this.host.nativeElement)) return;

      this.paths.forEach((el, index) => {
        const path = el.nativeElement;
        const length = path.getTotalLength();

        path.style.transition = 'none';
        path.style.strokeDashoffset = `${length}`;
        path.style.fillOpacity = '0';
        /* force reflow */
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        path.getBoundingClientRect();

        path.style.transition = `stroke-dashoffset ${segmentTime}ms ease-out ${segmentTime * index}ms`;
      });
    }, this.animationDuration + this.fillFadeTime + 30);
  }

  private isVisible(el: Element | null | undefined): boolean {
    if (!el || !(el as any).isConnected) return false;
    const s = getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;
    if ((el as Element).getClientRects().length === 0) return false;
    return true;
  }

  public async closeMiniChat() {
    this.showBubble = false;
   // await TextToSpeech.stop(); // stop previous
    this.replayBubbleAnim();
    this.onCloseBubble.emit();
  }

  public gotoFullChat() {
    this.showBubble = false;
    this.replayBubbleAnim();
    this.onMoreInfoClick.emit();

  }

  public botClicked() {
    this.replayBubbleAnim();
    this.onBotClicked.emit();
  }

  private replayBubbleAnim() {
    if (this.bubbleRef && this.bubbleRef.nativeElement) {
      const el = this.bubbleRef.nativeElement;
      el.style.animation = 'none'; // remove animation
      // force reflow so the browser registers the change
      void el.offsetWidth;
      el.style.animation = '';     // restore CSS (or set explicitly: 'fadeIn 1s ease-in')
    }
  }

  private async resetForRouteChange(): Promise<void> {
    try {
     // await TextToSpeech.stop(); // stop any ongoing TTS
    } catch { } // be tolerant if plugin is not ready

    // Clear bubble/message state
    this.showBubble = false;
    this._messageText = null;
    this.bubbleHintHtml = null;

    // Unlock & return to steady state face
    this.isFacePinned = false;
    this.setFace(this.isOnline ? VivaBotFaceState.happy : VivaBotFaceState.sad);

    // Clear any pending timers
    if (this.faceTimout) {
      clearTimeout(this.faceTimout);
      this.faceTimout = null;
    }

    // Optionally restart the idle/sleep timer
    this.startFaceTimer();

    // If bubble was mid-animation, ensure it doesn’t flicker back in
    this.replayBubbleAnim();
  }

  private stopAudio() {
   /* setTimeout(async () => {
      await TextToSpeech.stop(); // stop previous
    }, 10);*/
  }

  private async speakText(text: string) {

    return; /*

    if (!this.speechEnabled || !text) return;
    const platform = Capacitor.getPlatform();

    // Language hint from your translator service if available
    const lang = this.getDefaultBrowserLang();

    await TextToSpeech.stop(); // stop previous

    const plainText = this.htmlToSpeechText(text);
    

    await TextToSpeech.speak({
      text: plainText,
      lang,
      rate: 1.2,
      pitch: 1.0,
      volume:0.5,
      category: 'ambient',
      queueStrategy: QueueStrategy.Flush
    });*/
  }

  private getDefaultBrowserLang(): string {
    return this._TranslatorService.getCulture();
  }


  private htmlToSpeechText(html: string): string {
    if (!html) return '';

    // 1) insert line breaks where it makes sense
    let normalized = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n');

    // 2) let the browser parse & decode entities safely
    const tmp = document.createElement('div');
    tmp.innerHTML = normalized;

    // 3) extract text and tidy whitespace
    const text = (tmp.textContent || tmp.innerText || '')
      .replace(/\u00A0/g, ' ')     // nbsp -> space
      .replace(/[ \t]+\n/g, '\n')  // trim end-of-line spaces
      .replace(/\n{3,}/g, '\n\n')  // collapse big gaps
      .trim();

    return text;
  }
  public toggleSpeech() {
    this.speechEnabled = !this.speechEnabled;
  }
}

export enum VivaBotFaceState {
  happy = 'happy',
  sad = 'sad',
  error = 'error',
  sleep = 'sleep',
  thinking = 'thinking'
}